/**
 * G2eGente - Feed de Notícias (App Mobile do Funcionário)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { newsFeedAPI, API_URL } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft,
  Loader2,
  Newspaper,
  Megaphone,
  AlertTriangle,
  GraduationCap,
  ChevronRight,
  Bell,
  CheckCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { ReactionBar } from '../../components/news/reactions';
import { NewsPostHeader } from '../../components/news/NewsPostHeader';

const TIPO_CONFIG = {
  noticia: { label: 'Notícia', icon: Newspaper, color: 'bg-blue-100 text-blue-700', borderColor: 'border-l-blue-500' },
  comunicado: { label: 'Comunicado', icon: Megaphone, color: 'bg-green-100 text-green-700', borderColor: 'border-l-green-500' },
  alerta: { label: 'Alerta', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700', borderColor: 'border-l-amber-500' },
  treinamento: { label: 'Treinamento', icon: GraduationCap, color: 'bg-purple-100 text-purple-700', borderColor: 'border-l-purple-500' }
};

const PRIORIDADE_STYLE = {
  normal: '',
  alta: 'ring-2 ring-orange-200',
  urgente: 'ring-2 ring-red-300 bg-red-50/30'
};

const MAX_CHARS_PREVIEW = 120; // Limite de caracteres antes de mostrar "ver mais"

export default function EmployeeNewsFeed() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [feed, setFeed] = useState({ news: [], total: 0, nao_lidos: 0 });
  const [selectedNews, setSelectedNews] = useState(null);
  const [filter, setFilter] = useState('all'); // all, nao_lidos
  const [expandedCards, setExpandedCards] = useState({}); // Controla quais cards estão expandidos

  const toggleExpand = (newsId) => {
    setExpandedCards(prev => ({
      ...prev,
      [newsId]: !prev[newsId]
    }));
  };

  useEffect(() => {
    fetchFeed();
  }, []);

  const fetchFeed = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await newsFeedAPI.getEmployeeFeed();
      setFeed(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acesso restrito a funcionários');
      } else {
        toast.error('Erro ao carregar feed');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleOpenNews = async (news) => {
    try {
      const res = await newsFeedAPI.getEmployeeDetail(news.id);
      setSelectedNews({ ...news, ...res.data });
      // Atualizar localmente
      setFeed(prev => ({
        ...prev,
        news: prev.news.map(n => n.id === news.id ? { ...n, lido: true } : n),
        nao_lidos: Math.max(0, prev.nao_lidos - (news.lido ? 0 : 1))
      }));
    } catch (error) {
      toast.error('Erro ao carregar comunicado');
    }
  };

  const handleReaction = (newsId, data) => {
    setFeed(prev => ({ ...prev, news: prev.news.map(n => n.id === newsId ? { ...n, ...data } : n) }));
    setSelectedNews(prev => (prev && prev.id === newsId ? { ...prev, ...data } : prev));
  };

  const filteredNews = filter === 'all' 
    ? feed.news 
    : feed.news.filter(n => !n.lido);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  // Detalhe do comunicado selecionado
  if (selectedNews) {
    const TipoIcon = TIPO_CONFIG[selectedNews.tipo]?.icon || Newspaper;
    return (
      <div className="min-h-screen bg-slate-50" data-testid="news-detail">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="flex items-center gap-3 p-4">
            <Button variant="ghost" size="icon" onClick={() => setSelectedNews(null)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-semibold text-slate-900 truncate flex-1">
              {TIPO_CONFIG[selectedNews.tipo]?.label || 'Comunicado'}
            </h1>
          </div>
        </header>

        <main className="p-4 pb-20">
          <div className="mb-4">
            <NewsPostHeader
              news={selectedNews}
              tipoLabel={TIPO_CONFIG[selectedNews.tipo]?.label || 'Comunicado'}
              testId="news-detail-header"
            />
          </div>
          {selectedNews.imagem_capa && (
            <img 
              src={selectedNews.imagem_capa.startsWith('/') ? `${API_URL}${selectedNews.imagem_capa}` : selectedNews.imagem_capa} 
              alt="" 
              className="w-full aspect-video object-cover rounded-xl mb-4"
            />
          )}

          <div className="flex items-center gap-2 mb-3">
            <Badge className={TIPO_CONFIG[selectedNews.tipo]?.color}>
              <TipoIcon className="w-3 h-3 mr-1" />
              {TIPO_CONFIG[selectedNews.tipo]?.label}
            </Badge>
            {selectedNews.prioridade === 'urgente' && (
              <Badge className="bg-red-100 text-red-700">Urgente</Badge>
            )}
            {selectedNews.prioridade === 'alta' && (
              <Badge className="bg-orange-100 text-orange-700">Alta Prioridade</Badge>
            )}
          </div>

          <h2 className="text-xl font-bold text-slate-900 mb-2">{selectedNews.titulo}</h2>
          
          <p className="text-xs text-slate-500 mb-4">
            {selectedNews.data_publicacao 
              ? new Date(selectedNews.data_publicacao).toLocaleDateString('pt-BR', {
                  day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                })
              : 'Data não disponível'
            }
          </p>

          <div className="prose prose-sm max-w-none">
            <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
              {selectedNews.conteudo}
            </p>
          </div>

          <div className="mt-6 pt-4 border-t">
            <ReactionBar news={selectedNews} onChange={handleReaction} />
          </div>

          {selectedNews.anexos && selectedNews.anexos.length > 0 && (
            <div className="mt-6 pt-4 border-t">
              <h4 className="font-semibold text-slate-900 mb-2">Anexos</h4>
              <div className="space-y-2">
                {selectedNews.anexos.map((anexo, idx) => (
                  <a 
                    key={idx}
                    href={anexo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg hover:bg-slate-200"
                  >
                    <span className="text-sm text-cyan-600">{anexo.nome || `Anexo ${idx + 1}`}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // Lista de comunicados
  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="employee-news-feed">
      {/* Header */}
      <header className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white p-4 pt-8">
        <div className="flex items-center justify-between mb-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fetchFeed(true)}
            disabled={refreshing}
            className="text-white hover:bg-white/20"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <h1 className="text-xl font-bold">Feed de Notícias</h1>
        <p className="text-cyan-100 text-sm">Fique por dentro das novidades da empresa</p>
        
        {feed.nao_lidos > 0 && (
          <div className="flex items-center gap-2 mt-3 bg-white/20 rounded-lg p-2 w-fit">
            <Bell className="w-4 h-4" />
            <span className="text-sm font-medium">{feed.nao_lidos} não lido(s)</span>
          </div>
        )}
      </header>

      <main className="p-4">
        {/* Filtros */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-cyan-600' : ''}
          >
            Todos ({feed.total})
          </Button>
          <Button
            variant={filter === 'nao_lidos' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('nao_lidos')}
            className={filter === 'nao_lidos' ? 'bg-cyan-600' : ''}
          >
            Não lidos ({feed.nao_lidos})
          </Button>
        </div>

        {filteredNews.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {filter === 'nao_lidos' ? 'Tudo lido!' : 'Nenhum comunicado'}
              </h3>
              <p className="text-slate-500 text-sm">
                {filter === 'nao_lidos' 
                  ? 'Você já leu todos os comunicados' 
                  : 'Ainda não há comunicados para você'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredNews.map(news => {
              const isExpanded = expandedCards[news.id];
              const needsExpansion = news.conteudo && news.conteudo.length > MAX_CHARS_PREVIEW;
              const displayText = isExpanded || !needsExpansion 
                ? news.conteudo 
                : news.conteudo.substring(0, MAX_CHARS_PREVIEW) + '...';
              
              return (
                <Card 
                  key={news.id}
                  data-testid={`news-card-${news.id}`}
                  className={`border-slate-200 overflow-hidden ${PRIORIDADE_STYLE[news.prioridade]} ${!news.lido ? 'bg-cyan-50/30' : ''}`}
                >
                  <div className="px-4 pt-3 pb-3">
                    <NewsPostHeader
                      news={news}
                      tipoLabel={TIPO_CONFIG[news.tipo]?.label || news.tipo}
                      unread={!news.lido}
                      testId={`news-header-${news.id}`}
                    />
                  </div>

                  {news.imagem_capa && (
                    <div 
                      className="w-full aspect-video bg-slate-100 cursor-pointer"
                      onClick={() => handleOpenNews(news)}
                    >
                      <img 
                        src={news.imagem_capa.startsWith('/') ? `${API_URL}${news.imagem_capa}` : news.imagem_capa} 
                        alt={news.titulo}
                        className="w-full h-full object-cover"
                        data-testid={`news-image-${news.id}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  <CardContent className="p-4">
                    {news.prioridade !== 'normal' && (
                      <Badge className={`mb-2 ${news.prioridade === 'urgente' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                        {news.prioridade === 'urgente' ? 'Urgente' : 'Alta prioridade'}
                      </Badge>
                    )}
                    {/* Título */}
                    <h4 
                      className={`font-semibold text-slate-900 mb-2 cursor-pointer hover:text-cyan-700 ${!news.lido ? 'text-cyan-900' : ''}`}
                      onClick={() => handleOpenNews(news)}
                    >
                      {news.titulo}
                    </h4>
                    
                    {/* Conteúdo (estilo Instagram) */}
                    <div className="text-sm text-slate-600">
                      <p className="whitespace-pre-wrap" data-testid={`news-text-${news.id}`}>{displayText}</p>
                      
                      {needsExpansion && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(news.id);
                          }}
                          className="text-cyan-600 font-medium mt-1 flex items-center gap-1 hover:text-cyan-700"
                          data-testid={`news-toggle-${news.id}`}
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="w-4 h-4" />
                              ver menos
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              ver mais
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    
                    <div className="mt-3 pt-2 border-t border-slate-100">
                      <ReactionBar news={news} onChange={handleReaction} />
                    </div>

                    {/* Footer: Ações */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                      <div className="flex items-center gap-2">
                        {news.lido ? (
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            Lido
                          </span>
                        ) : (
                          <span className="text-xs text-cyan-600 font-medium">Novo</span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenNews(news)}
                        className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                      >
                        Ver detalhes
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
